import { SsoType } from '@diritto/common/account/constants/sso.constant';
import { CreateUserMarketingAgreementDto } from '@diritto/common/account/dtos/create-user-marketing-agreement.dto';
import { SignUpResponseDto } from '@diritto/common/account/dtos/sign-up-response.dto';
import { UserSignUpAgreementDto } from '@diritto/common/account/dtos/user-sign-up-agreement.dto';
import { BestType } from '@diritto/common/best/constants';
import {
  CategoryType,
  ContentDetailTabType,
  ContentOrderType,
  StudioContentsTabType,
} from '@diritto/common/content/constants';
import { ContestStep } from '@diritto/common/content/types/content-contest-info-types';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { SectionDesign } from '@diritto/common/home/constants/section-constants';
import { CategoryNavigationMenu } from '@diritto/common/navigation/constant';
import { ExploreNavigationMenu, ExploreSubMenu } from '@diritto/common/navigation/explore';
import { NotiExtraDomainType } from '@diritto/common/notification/constants';
import { PreviewModeType } from '@diritto/common/previewer/constants';
import { RecentlyReadContentOrder } from '@diritto/common/recentlyRead/constants';
import { ReplyOrder } from '@diritto/common/reply/constants';
import { RollingAction as RollingActionType } from '@diritto/common/rolling/constants';
import { FavoriteContentOrder } from '@diritto/common/userReaction/constants';
import {
  ViewerFont,
  ViewerFontSize,
  ViewerLineHeight,
  ViewerPadding,
  ViewerParagraphIndent,
  ViewerTheme,
  ViewerViewType,
} from '@diritto/common/viewer/constants/viewer.constant';

export enum ContentListType {
  DirittoRecommend = 'DirittoRecommend', // 디리토 추천
  LatestUpdated = 'LatestUpdated', // 최신 업데이트
  Best = 'Best', // 베스트
  Explore = 'Explore', // 탐색
  Recently = 'Recently', // 최근 본
  Favorites = 'Favorites', // 선호작품
  SearchResult = 'SearchResult', // 검색 결과
  Sitemap = 'Sitemap', // 사이트맵 - 전체 작품 목록

  SectionReply = 'SectionReply', // 섹션 댓글
  SectionContent = 'SectionContent', // 섹션 작품
  SectionRecentlyReadContent = 'SectionRecentlyReadContent', // 섹션 최근 본 작품
  Studio = 'Studio',
}

export enum QuickMenuType {
  Event = 'Event',
  AttendanceCheck = 'AttendanceCheck',
  QuestRank = 'QuestRank',
}

export enum TrackerPosition {
  Home = 'Home',
  Latest = 'Latest',
  Explorer = 'Explorer',
  Best = 'Best',
  My = 'My',
  Viewer = 'Viewer',
  Reply = 'Reply',
  ReReply = 'ReReply',
  ViewerBottomBar = 'ViewerBottomBar',
  RollingViewer = 'RollingViewer',
  RollingEditor = 'RollingEditor',
  EventDetail = 'EventDetail',
  FancyShareDetail = 'FancyShareDetail',
  ContentDetail = 'ContentDetail',
  EditContent = 'EditContent',
  AttendanceCheck = 'AttendanceCheck',
  QuestRanking = 'QuestRanking',
  ProfileDetail = 'ProfileDetail',
  Search = 'Search',
  Section = 'Section',
  SectionDetail = 'SectionDetail',
  UserMenu = 'UserMenu',
  Contest = 'Contest',
  Promotion = 'Promotion',
  ProfileSetting = 'ProfileSetting',

  StudioEditor = 'StudioEditor', // Deprecated

  DirittoStudioLogo = 'DirittoStudioLogo',
  DirittoStudioMenu = 'DirittoStudioMenu',
  DirittoStudioContentSerializedList = 'DirittoStudioContentSerializedList',
  DirittoStudioContentCompletedOrPublishedList = 'DirittoStudioContentCompletedOrPublishedList',
  DirittoStudioContentPickAndChooList = 'DirittoStudioContentPickAndChooList',
  DirittoStudioContentAllList = 'DirittoStudioContentAllList',
  DirittoStudioContentsMoreMenu = 'ContentEpisodesMoreMenu',
  DirittoStudioContentEpisodeList = 'DirittoStudioContentEpisodeList',
  DirittoStudioContentEpisodePublicList = 'DirittoStudioContentEpisodePublicList',
  DirittoStudioContentEpisodeReservedList = 'DirittoStudioContentEpisodeReservedList',
  DirittoStudioContentEpisodeSavedList = 'DirittoStudioContentEpisodeSavedList',
  DirittoStudioContentEpisodesMoreMenu = 'DirittoStudioContentEpisodesMoreMenu',
  DirittoStudioContentEpisodesLeftMenu = 'DirittoStudioContentEpisodesLeftMenu',
  DirittoStudioContentEpisodesMobileBottomMenu = 'DirittoStudioContentEpisodesMobileBottomMenu',
}

export interface TrackerExtraInfo {
  trackerPosition: TrackerPosition;
}

export interface TrackerExtraInfoProps {
  trackerExtraInfo: TrackerExtraInfo;
}

export interface ContentListItemSelectParams {
  [ContentListType.DirittoRecommend]: Record<string, never>;
  [ContentListType.LatestUpdated]: { category: CategoryNavigationMenu };
  [ContentListType.Best]: { category: CategoryNavigationMenu; bestType: BestType };
  [ContentListType.Recently]: Record<string, never>;
  [ContentListType.Favorites]: Record<string, never>;
  [ContentListType.Explore]: {
    categoryMenu: CategoryNavigationMenu;
    exploreMenu: ExploreNavigationMenu;
    exploreSubMenu?: ExploreSubMenu;
  };
  [ContentListType.SearchResult]: { numOfTotalSearchResults: number; searchKeyword: string };
  [ContentListType.Sitemap]: Record<string, never>;
  [ContentListType.SectionReply]: Record<string, never>;
  [ContentListType.SectionContent]: {
    sectionId: number;
    sectionTitle: string;
    sectionDesign: SectionDesign;
  };
  [ContentListType.SectionRecentlyReadContent]: Record<string, never>;
  [ContentListType.Studio]: Record<string, never>;
}

export enum ClickAction {
  ToContentDetail = 'ToContentDetail',
  ToMyProfile = 'ToMyProfile',
  ToHelp = 'ToHelp',
  ToSetting = 'ToSetting',
  ToEvent = 'ToEvent',
  ToReply = 'ToReply',
  ToFirstPage = 'ToFirstPage',
  ToMessageList = 'ToMessageList',
  ToAttendanceCheck = 'ToAttendanceCheck',
  ToDirittoStudioManual = 'ToDirittoStudioManual',
  ToDirittoStudioSurvey = 'ToDirittoStudioSurvey',
  ToDirittoStudio = 'ToDirittoStudio',
  ToDiritto = 'ToDiritto',
  ToDirittoContentDetail = 'ToDirittoContentDetail',
  ToDirittoStudioContentAdd = 'ToDirittoStudioContentAdd',
  ToDirittoStudioContentEdit = 'ToDirittoStudioContentEdit',
  ToDirittoStudioContentEpisodes = 'ToDirittoStudioContentEpisodes',
  ToDirittoStudioContentSynopsis = 'ToDirittoStudioContentSynopsis',
  ToDirittoStudioContentEpisodeEdit = 'ToDirittoStudioContentEpisodeEdit',
  ToDirittoStudioContentEpisodeAdd = 'ToDirittoStudioContentEpisodeAdd',
  ToProposeContract = 'ToProposeContract',
  ToFavoriteCategory = 'ToFavoriteCategory',
  SaveContent = 'SaveContent',
  SaveContentSynopsis = 'SaveContentSynopsis',
  EventButton = 'EventButton',
  DeleteContent = 'DeleteContent',
  DeleteEpisode = 'DeleteEpisode',
  OnRollingReaction = 'OnRollingReaction',
  OffRollingReaction = 'OffRollingReaction',
  ShareRolling = 'ShareRolling',
  ShowContentDesc = 'ShowContentDesc',
  MyContentSelectEditButton = 'MyContentSelectEditButton',
}

export enum ClickTargetType {
  Title = 'Title',
  Icon = 'Icon',
  Cover = 'Cover',
  Banner = 'Banner',
  Modal = 'Modal',
  Button = 'Button',
  Menu = 'Menu',
  Tab = 'Tab',
  MoreMenu = 'MoreMenu',
  EmptyStateButton = 'EmptyStateButton',
}

export enum TrackingEventType {
  // user activation events
  SignIn = 'Sign In',
  SignOut = 'Sign Out',
  SignUp = 'Sign Up',
  Withdrawal = 'Withdrawal',
  CompleteSSOSignUp = 'Complete SSO Sign Up',
  CompleteSignUpAgreement = 'Complete Sign Up Agreement',
  CompleteUserCertification = 'Complete User Certification',
  CompleteLateUserCertification = 'Complete Late User Certification',
  LinkSsoAtSign = 'Link Sso At Sign',
  LinkSso = 'Link Sso',

  Click = 'Click',

  ClickIntroModal = 'Click Intro Modal',

  ClickToContentDetail = 'Click To Content Detail',
  ClickToEpisodeViewer = 'Click To Episode Viewer',
  ClickToProfileDetail = 'Click To Profile Detail',
  ClickToRollingAllList = 'Click To Rolling All List',

  ClickAttendanceCheckParticipate = 'Click Attendance Check Participate',
  ClickMarketingAgreement = 'Click Marketing Agreement',
  ClickItemOnHomeSection = 'Click Item On Home Section',
  ClickMoreLinkOnHomeSection = 'Click More Link On Home Section',
  ClickAppInstallBottomModal = 'Click App Install Bottom Modal',
  ClickViewerPageViewIntroModal = 'Click Viewer Page View Intro Modal',
  ClickAttendanceCheckInduceBottomModal = 'Click Attend Check Induce Bottom Modal',
  ClickAuthorSurveyBottomModal = 'Click Author Survey Bottom Modal',
  CloseAppInstallBottomModal = 'Close App Install Bottom Modal',
  ClickAppPushOnBottomModal = 'Click App Push On Bottom Modal',
  CloseAppPushOnBottomModal = 'Close App Push On Bottom Modal',
  ClickAppPushOnBanner = 'Click App Push On Banner',
  CloseAppPushOnBanner = 'Close App Push On Banner',
  ClickProfileEmailCopy = 'Click Profile Email Copy',
  ClickProfileEmail = 'Click Profile Email',
  ClickProfileTwitter = 'Click Profile Twitter',
  ClickCertificationTransfer = 'Click Certification Transfer',
  ClickSectionCustomQuickMenu = 'Click Section Custom Quick Menu',
  ClickSectionQuickMenu = 'Click Section Quick Menu',
  ClickSectionKeyword = 'Click Section Keyword',
  ClickSectionFavoriteCategory = 'Click Section Favorite Category',
  ClickProfileSettingFavoriteCategory = 'Click Profile Setting Favorite Category',
  ClickContentEditToChange = 'Click Content Edit To Change',
  ClickEventButton = 'Click Event Button',
  ClickRollingReadMore = 'Click Rolling Read More',
  ClickToReply = 'Click To Reply',
  CloseProposeContract = 'Close Propose Contract',

  ViewHome = 'View Home',
  ViewExplorer = 'View Explorer',
  ViewFavorite = 'View Favorite',
  ViewFavoriteEdit = 'View Favorite Edit',
  ViewRecentlyRead = 'View Recently Read',
  ViewRecentlyReadEdit = 'View Recently Read Edit',
  ViewRanking = 'View Ranking',
  ViewActivityRankingAll = 'View Activity Ranking All',
  ViewActivityRankingMy = 'View Activity Ranking My',
  ViewContent = 'View Content',
  ViewEpisode = 'View Episode',
  ViewStudioContentList = 'View Studio Content List',
  ViewStudioContentAdd = 'View Studio Content Add',
  ViewStudioContentEdit = 'View Studio Content Edit',
  ViewStudioContentEpisodeList = 'View Studio Content Episode List',
  ViewStudioContentSynopsis = 'View Studio Content Synopsis',
  ViewStudioEpisodeAdd = 'View Studio Episode Add',
  ViewStudioEpisodeEdit = 'View Studio Episode Edit',
  ViewInbox = 'View Inbox',
  ViewHelp = 'View Help',
  ViewFancyShareDetail = 'View FancyShareDetail',
  ViewFancyShareList = 'View FancyShareList',
  ViewRollingList = 'View RollingList',
  ViewRolling = 'View Rolling',
  ViewActivityPoint = 'View Activity Point',
  ViewAttendanceCheck = 'View Attendance Check',
  ViewQuestAll = 'View Quest Ranking All',
  ViewQuestOngoingSeason = 'View Quest Ongoing Season',
  ViewQuestEndedSeason = 'View Quest Ended Season',
  ViewSearch = 'View Search',
  ViewContentReply = 'View Content Reply',
  ViewEpisodeReply = 'View Episode Reply',
  ViewEpisodeReReply = 'View Episode ReReply',
  ViewPromotion = 'View Promotion',

  // user engagement events
  CreateNewProfile = 'Create New Profile',
  UpdateProfile = 'Update Profile',
  ChangeViewerSetting = 'Change Viewer Setting',
  ChangeHomeViewMode = 'Change Home View Mode',
  CreateReply = 'Create Reply',
  UpdateReply = 'Update Reply',
  DeleteReply = 'Delete Reply',
  LikeReply = 'Like Reply',
  UnlikeReply = 'Unlike Reply',
  SetFavoriteContent = 'Set Favorite Content',
  ReleaseFavoriteContent = 'Release Favorite Content',
  RecommendEpisode = 'Recommend Episode',
  UnrecommendEpisode = 'Unrecommend Episode',

  SetCategoryFilter = 'Set Category Filter',
  SetOrderFilter = 'Set Order Filter',
  SetNotiOnlyFilter = 'Set Noti Only Filter',
  SetCompletedOnlyFilter = 'Set Completed Only Filter',
  SetPrivateOnlyFilter = 'Set Private Only Filter',

  CreateFancyShare = 'Create FancyShare',
  UpdateFancyShare = 'Update FancyShare',
  DeleteFancyShare = 'Delete FancyShare',
  LikeFancyShare = 'Like FancyShare',
  UnlikeFancyShare = 'Unlike FancyShare',
  ShareFancyShare = 'Share FancyShare',
  PromotionShare = 'Promotion Share',

  RollingAction = 'Rolling Action',

  SelectContentListItem = 'Select Content List Item',
  SelectContentBannerItem = 'Select Content Banner Item',
  SelectEventContentItem = 'Select Event Content Item',
  SelectExclusiveContentItem = 'Select Exclusive Content Item',

  ClickContentDetailTab = 'Click Content Detail Tab',
  ClickContentDetailEpisodeOrderTab = 'Click Content Detail EpisodeOrder Tab',
  ClickContentDetailReplyOrderTab = 'Click Content Detail ReplyOrder Tab',

  FollowProfile = 'Follow Profile',
  UnfollowProfile = 'Unfollow Profile',
  AddNewEpisodeNoti = 'Add New Episode Noti',
  DeleteNewEpisodeNoti = 'Delete New Episode Noti',
  DeleteBulkNewEpisodeNoti = 'Delete Bulk New Episode Noti',
  // app events
  Share = 'Share',
  FileDownload = 'File Download',
  ClickInAppReview = 'Click In App Review',
  ClickPush = 'Click Push Notification',

  // author engagement events
  CreateContent = 'Create Content',
  UpdateContent = 'Update Content',
  DeleteContent = 'Delete Content',
  CreateEpisode = 'Create Episode',
  UpdateEpisode = 'Update Episode',
  DeleteEpisode = 'Delete Episode',

  // search events
  Search = 'Search',

  // event page
  ViewEvent = 'View Event',
  ClickAppInstallEventHeader = 'Click App Install Event Page',

  ClickReplyEmojiShortCut = 'Click Reply Emoji ShortCut',
  ClickEpisodeListButtonClick = 'Click Episode List Button Click',

  ClickAttendanceCheck = 'Click Attendance Check',
  ClickChangeAttendanceCheckMonth = 'Click Change Attendance Check  Month',

  ClickInboxItem = 'Click Inbox Item',

  ClickDeleteContent = 'Click Delete Content',
  ClickDeleteEpisode = 'Click Delete Episode',
  ClickEditorPreview = 'Click Editor Preview',
  SwitchPreviewMode = 'Switch Preview Mode',
  // ClickPrevEpisodeButton = 'Click Prev Episode Button',
  ClickNextEpisodeButton = 'Click Next Episode Button',

  ShowedContentCoverEditorImage = 'Showed Content Cover Editor Image',
  ShowedFancyShareBackgroundImage = 'Showed FancyShare Background Image',

  // promotion
  Promotion = 'Promotion',
  CreatePromotionReply = 'Create Promotion Reply',
  DeletePromotionReply = 'Delete Promotion Reply',
  LikePromotionReply = 'Like Promotion Reply',
  UnlikePromotionReply = 'Unlike Promotion Reply',
  SharePromotion = 'Share Promotion',

  // pickandchoo 2
  ClickPickAndChooBanner = 'Click PickAndChoo Banner',
  ClickApplyButton = 'Click Apply Button',
  ClickPnCStudioContentNavigation = 'Click PnC Studio Content Navigation',

  ViewPromotionPickAndChoo = 'View Promotion PickAndChoo',
  ViewPnCStudioContentEdit = 'View PnC Studio Content Edit',
  ViewPnCStudioContentSynopsis = 'View PnC Studio Content Synopsis',
}

export interface TrackingEventParams {
  [TrackingEventType.Click]: {
    action: ClickAction;
    clickType?: ClickTargetType;
    label?: string;
  } & TrackerExtraInfoProps;

  // user activation events
  [TrackingEventType.SignIn]: { userId: number; ssoType: SsoType };
  [TrackingEventType.SignOut]: undefined;
  [TrackingEventType.SignUp]: SignUpResponseDto;
  [TrackingEventType.Withdrawal]: undefined;
  [TrackingEventType.ClickMarketingAgreement]: { agree: boolean; agreeNight: boolean };
  [TrackingEventType.CompleteSSOSignUp]: { ssoType: SsoType };
  [TrackingEventType.CompleteSignUpAgreement]: {
    ssoType: SsoType;
    userSignUpAgreement: UserSignUpAgreementDto;
    userMarketingAgreement: CreateUserMarketingAgreementDto;
  };
  [TrackingEventType.CompleteUserCertification]: { ssoType: SsoType };
  [TrackingEventType.CompleteLateUserCertification]: undefined;
  [TrackingEventType.LinkSsoAtSign]: undefined;
  [TrackingEventType.LinkSso]: undefined;

  [TrackingEventType.ClickToContentDetail]: { contentId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickToEpisodeViewer]: { episodeId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickToProfileDetail]: { profileId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickToRollingAllList]: { rollingId?: number } & TrackerExtraInfoProps;

  [TrackingEventType.ClickIntroModal]: { introType: number };

  [TrackingEventType.ClickAttendanceCheckParticipate]: { attendanceCheckMonthId: number; yearMonth: string };
  [TrackingEventType.ClickMoreLinkOnHomeSection]: { sectionId: number; title: string } & TrackerExtraInfoProps;
  [TrackingEventType.ClickItemOnHomeSection]: {
    listItemIndex: number;
    sectionId: number;
    sectionDesign: SectionDesign;
  } & TrackerExtraInfoProps;
  [TrackingEventType.ClickAppInstallBottomModal]: undefined;
  [TrackingEventType.ClickAttendanceCheckInduceBottomModal]: {
    clickType: 'close' | 'goToActivityPoint' | 'goToAttendanceCheck';
  };
  [TrackingEventType.ClickAuthorSurveyBottomModal]: {
    clickType: 'close' | 'goToSurvey';
  };
  [TrackingEventType.CloseAppInstallBottomModal]: undefined;
  [TrackingEventType.ClickAppPushOnBottomModal]: undefined;
  [TrackingEventType.CloseAppPushOnBottomModal]: undefined;
  [TrackingEventType.ClickAppPushOnBanner]: undefined;
  [TrackingEventType.CloseAppPushOnBanner]: undefined;
  [TrackingEventType.ClickProfileTwitter]: { profileId: number };
  [TrackingEventType.ClickProfileEmail]: { profileId: number };
  [TrackingEventType.ClickProfileEmailCopy]: { profileId: number };
  [TrackingEventType.ClickCertificationTransfer]: undefined;
  [TrackingEventType.ClickSectionQuickMenu]: { quickMenu: QuickMenuType };
  [TrackingEventType.ClickSectionCustomQuickMenu]: undefined;
  [TrackingEventType.ClickSectionKeyword]: { keyword: string };
  [TrackingEventType.ClickSectionFavoriteCategory]: { categoryIds: number[] } & TrackerExtraInfoProps;
  [TrackingEventType.ClickProfileSettingFavoriteCategory]: { categoryIds: number[] } & TrackerExtraInfoProps;
  [TrackingEventType.ClickEventButton]: { eventId: number; url: string };
  [TrackingEventType.ClickRollingReadMore]: { rollingId: number };
  [TrackingEventType.ClickToReply]: { replyId: number } & TrackerExtraInfoProps;
  [TrackingEventType.CloseProposeContract]: undefined;

  // user engagement events
  [TrackingEventType.CreateNewProfile]: undefined;
  [TrackingEventType.UpdateProfile]: {
    profileId: number;
    profileNickname: string;
    profileDescription?: string;
    profileEmail?: string;
    profileTwitter?: string;
  };
  [TrackingEventType.ChangeViewerSetting]: undefined;
  [TrackingEventType.ChangeHomeViewMode]: { viewMode: string } & TrackerExtraInfoProps;
  [TrackingEventType.CreateReply]: { contentId: number; episodeId?: number; replyId: number };
  [TrackingEventType.UpdateReply]: { contentId: number; episodeId?: number; replyId: number };
  [TrackingEventType.DeleteReply]: { contentId: number; episodeId?: number; replyId: number };
  [TrackingEventType.LikeReply]: { replyId: number };
  [TrackingEventType.UnlikeReply]: { replyId: number };
  [TrackingEventType.SetFavoriteContent]: { contentId: number; profileId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ReleaseFavoriteContent]: {
    contentId: number;
    profileId: number;
  } & TrackerExtraInfoProps;
  [TrackingEventType.RecommendEpisode]: { episodeId: number };
  [TrackingEventType.UnrecommendEpisode]: { episodeId: number };

  [TrackingEventType.SetCategoryFilter]: { categoryType: CategoryType } & TrackerExtraInfoProps;
  [TrackingEventType.SetOrderFilter]: {
    orderBy: FavoriteContentOrder | RecentlyReadContentOrder | ContentOrderType;
  } & TrackerExtraInfoProps;
  [TrackingEventType.SetNotiOnlyFilter]: { enabled: boolean } & TrackerExtraInfoProps;
  [TrackingEventType.SetCompletedOnlyFilter]: { enabled: boolean } & TrackerExtraInfoProps;
  [TrackingEventType.SetPrivateOnlyFilter]: { enabled: boolean } & TrackerExtraInfoProps;

  [TrackingEventType.CreateFancyShare]: { contentId: number; episodeId: number; fancyShareId: number };
  [TrackingEventType.UpdateFancyShare]: { fancyShareId: number };
  [TrackingEventType.DeleteFancyShare]: { fancyShareId: number };
  [TrackingEventType.LikeFancyShare]: { fancyShareId: number };
  [TrackingEventType.UnlikeFancyShare]: { fancyShareId: number };
  [TrackingEventType.ShareFancyShare]: { fancyShareId: number; contentId?: number };
  [TrackingEventType.PromotionShare]: { promotionId: number; itemIds: (number | undefined)[]; fromMobile: boolean };

  [TrackingEventType.ViewHome]: undefined;
  [TrackingEventType.ViewHelp]: undefined;
  [TrackingEventType.ViewExplorer]: {
    exploreMenu: ExploreNavigationMenu;
    categoryMenu: CategoryNavigationMenu;
    exploreSubMenu?: ExploreSubMenu;
  };
  [TrackingEventType.ViewRanking]: { categoryMenu: CategoryNavigationMenu; bestType: BestType };
  [TrackingEventType.ViewRecentlyRead]: undefined;
  [TrackingEventType.ViewRecentlyReadEdit]: undefined;
  [TrackingEventType.ViewFavorite]: undefined;
  [TrackingEventType.ViewFavoriteEdit]: undefined;
  [TrackingEventType.ViewInbox]: undefined;
  [TrackingEventType.ViewRolling]: { rollingId: number };

  [TrackingEventType.ViewStudioContentList]: undefined;
  [TrackingEventType.ViewStudioContentAdd]: undefined;
  [TrackingEventType.ViewStudioContentEdit]: { contentId: number };
  [TrackingEventType.ViewStudioContentEpisodeList]: { contentId: number };
  [TrackingEventType.ViewStudioContentSynopsis]: { contentId: number };
  [TrackingEventType.ViewStudioEpisodeAdd]: { contentId: number };
  [TrackingEventType.ViewStudioEpisodeEdit]: { contentId: number; episodeId: number };
  [TrackingEventType.ViewSearch]: undefined;
  [TrackingEventType.ViewContentReply]: { contentId: number };
  [TrackingEventType.ViewEpisodeReply]: { contentId: number; episodeId: number };
  [TrackingEventType.ViewEpisodeReReply]: { contentId: number; episodeId: number; replyId: number };

  [TrackingEventType.ViewContent]: { contentId: number };
  [TrackingEventType.SelectContentListItem]: {
    listType: ContentListType;
    contentId: number;
    listItemIndex: number;
    params?: ContentListItemSelectParams[ContentListType];
  } & TrackerExtraInfoProps;
  [TrackingEventType.SelectContentBannerItem]: {
    bannerTitle: string;
    contentId: number;
    listItemIndex: number;
    params?: ContentListItemSelectParams[ContentListType];
  } & TrackerExtraInfoProps;
  [TrackingEventType.SelectEventContentItem]: {
    sectionId: number;
    sectionTitle: string;
    contentId: number;
  } & TrackerExtraInfoProps;
  [TrackingEventType.SelectExclusiveContentItem]: {
    sectionId: number;
    sectionTitle: string;
    contentId: number;
    listItemIndex: number;
  } & TrackerExtraInfoProps;

  [TrackingEventType.ViewEpisode]: { contentId: number; episodeId: number };

  [TrackingEventType.ViewFancyShareList]: TrackerExtraInfoProps;

  [TrackingEventType.ViewActivityPoint]: undefined;
  [TrackingEventType.ViewAttendanceCheck]: { yearMonth: string };
  [TrackingEventType.ViewActivityRankingAll]: undefined;
  [TrackingEventType.ViewActivityRankingMy]: undefined;
  [TrackingEventType.ViewQuestAll]: undefined;
  [TrackingEventType.ViewQuestOngoingSeason]: undefined;
  [TrackingEventType.ViewQuestEndedSeason]: undefined;
  [TrackingEventType.ViewFancyShareDetail]: { fancyShareId: number };
  [TrackingEventType.ClickContentDetailTab]: { contentId: number; tabType: ContentDetailTabType };
  [TrackingEventType.ClickContentDetailEpisodeOrderTab]: { contentId: number; tabType: EpisodeOrderType };
  [TrackingEventType.ClickContentDetailReplyOrderTab]: { contentId: number; tabType: ReplyOrder };

  [TrackingEventType.FollowProfile]: { profileId: number };
  [TrackingEventType.UnfollowProfile]: { profileId: number };
  [TrackingEventType.AddNewEpisodeNoti]: { contentId: number } & TrackerExtraInfoProps;
  [TrackingEventType.DeleteNewEpisodeNoti]: { contentId: number } & TrackerExtraInfoProps;
  [TrackingEventType.DeleteBulkNewEpisodeNoti]: { contentIds: number[] } & TrackerExtraInfoProps;

  // author engagement events
  [TrackingEventType.CreateContent]: { contentId: number };
  [TrackingEventType.UpdateContent]: { contentId: number };
  [TrackingEventType.DeleteContent]: { contentId: number } & TrackerExtraInfoProps;
  [TrackingEventType.CreateEpisode]: { contentId: number; episodeId: number };
  [TrackingEventType.UpdateEpisode]: { contentId: number; episodeId: number };
  [TrackingEventType.DeleteEpisode]: { contentId: number; episodeId: number } & TrackerExtraInfoProps;
  // search events
  [TrackingEventType.Search]: { searchKeyword: string };

  [TrackingEventType.ClickPush]: { title: string; deeplink: string };
  [TrackingEventType.ClickInAppReview]: { result: boolean };
  [TrackingEventType.Share]: { title: string; text: string; url: string };
  [TrackingEventType.FileDownload]: { url: string };

  // event page
  [TrackingEventType.ViewEvent]: { eventId: number };
  [TrackingEventType.ClickAppInstallEventHeader]: undefined;

  [TrackingEventType.ClickChangeAttendanceCheckMonth]: { yearMonth: string };
  [TrackingEventType.ClickReplyEmojiShortCut]: { emoji: string };
  [TrackingEventType.ClickEpisodeListButtonClick]: { contentId: number; episodeId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickAttendanceCheck]: { attendAt: Date };
  [TrackingEventType.ClickDeleteContent]: { contentId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickDeleteEpisode]: { contentId: number; episodeId: number } & TrackerExtraInfoProps;
  [TrackingEventType.ClickContentEditToChange]: {
    contentId?: number;
    target: 'setReservedHideContentsAt' | 'changeReservedHideContentsAt';
  } & TrackerExtraInfoProps;
  [TrackingEventType.ClickViewerPageViewIntroModal]: { confirm: boolean };

  [TrackingEventType.ClickInboxItem]: { title: string; sentAt: Date; type: NotiExtraDomainType };
  [TrackingEventType.ClickEditorPreview]: { contentId: number; episodeId?: number };
  [TrackingEventType.SwitchPreviewMode]: { previewMode: PreviewModeType; contentId: number; episodeId?: number };
  [TrackingEventType.ClickNextEpisodeButton]: { contentId: number; episodeId: number } & TrackerExtraInfoProps;

  [TrackingEventType.ShowedContentCoverEditorImage]: { index: number };
  [TrackingEventType.ShowedFancyShareBackgroundImage]: { index: number };

  [TrackingEventType.ViewRollingList]: TrackerExtraInfoProps;
  [TrackingEventType.RollingAction]: { action: RollingActionType; rollingId: number };
  [TrackingEventType.ViewPromotion]: { promotionId: number; slug: string };

  // Promotion
  [TrackingEventType.Promotion]: { itemId: number; promotionId: number };
  [TrackingEventType.CreatePromotionReply]: { promotionId: number; replyId: string };
  [TrackingEventType.DeletePromotionReply]: { promotionId: number; replyId: string };
  [TrackingEventType.LikePromotionReply]: { replyId: string };
  [TrackingEventType.UnlikePromotionReply]: { replyId: string };
  [TrackingEventType.SharePromotion]: { promotionId: number };

  // pickandchoo 2
  [TrackingEventType.ClickPickAndChooBanner]: { contestId: number };
  [TrackingEventType.ClickApplyButton]: { contestId: number; tabType?: StudioContentsTabType };

  [TrackingEventType.ViewPromotionPickAndChoo]: { contestId: number };
  [TrackingEventType.ViewPnCStudioContentEdit]: { contestId: number };
  [TrackingEventType.ViewPnCStudioContentSynopsis]: { contestId: number };
  [TrackingEventType.ClickPnCStudioContentNavigation]: { contestId: number; step: ContestStep };
}

export type TrackingViewerSettingParams =
  | ViewerTheme
  | ViewerFont
  | ViewerFontSize
  | ViewerLineHeight
  | ViewerParagraphIndent
  | ViewerPadding
  | ViewerViewType
  | boolean;
